import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { add, endOfYear, startOfYear } from 'date-fns';
import React from 'react';
import { ArrowRight, CheckCircle } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { enumToIcon, enumToStatusType } from '../../../types/common';
import { ProjectSummary } from '../../../types/project';
import { Calendar, CalendarEvent, CalendarView } from '../../global/calendar/Calendar';
import { Badge, StatusIcon } from '../../global/Status';

const useProjectEventClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        color: theme.colors.neutral[1],
        '& > span': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...theme.fonts.caption,
            color: theme.colors.neutral[2],
        },
        '&.tooltip > span': { whiteSpace: 'normal' },
        '& > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.fonts.labelSemiBold,
        },
        '& > hr': {
            margin: '8px 0',
            borderColor: theme.colors.neutral[6],
        },
    },
    badges: {
        display: 'flex',
        gap: 5,
    },
    date: {
        display: 'flex',
        alignItems: 'center',
    },
});

type ProjectData = {
    project: ProjectSummary;
    forecast?: 'internal' | 'external';
};

const colors = Object.freeze({
    'past due': theme.colors.red[1],
    forecast: theme.colors.neutral[3],
    'ready to inspect': theme.colors.blue[2],
    'in progress': theme.colors.green[4],
    'awaiting report': theme.colors.amber[2],
    'under review': theme.colors.amber[2],
    completed: theme.colors.neutral[2],
} as { [key: string]: string });

// TODO: This component is almost the same as ProjectCard in this same directory (I will try to reuse it in the future)
const ProjectEvent = (props: { event: CalendarEvent; view: CalendarView; tooltip: boolean }) => {
    const classes = useProjectEventClasses();
    const { t, Trans } = usePrefixTranslation(['project', 'common'], {
        project: 'common.ProjectCalendar.ProjectEvent',
    });
    const { project, forecast } = props.event.data as ProjectData;
    const type = enumToStatusType[!!forecast ? 'forecast' : project.status!];
    const icon = enumToIcon[!!forecast ? 'forecast' : project.status!];
    const cls = { tooltip: props.tooltip };

    const date = cls.tooltip && (
        <Trans
            i18nKey={!!forecast ? 'date' : 'range'}
            values={{
                date: forecast === 'internal' ? project.internal_due_date : project.external_due_date,
                start: project.status !== 'completed' ? project.estimated_start_date : project.in_progress_date,
                end: project.status !== 'completed' ? project.estimated_completed_date : project.completed_date,
            }}
            components={{ arrow: <ArrowRight style={{ width: 15, height: 15 }} /> }}
        />
    );

    return (
        <div className={clsx(classes.root, cls)}>
            <span>{props.event.name}</span>
            <span>{project.code}</span>
            <div className={classes.badges}>
                {!forecast && <StatusIcon type={type} icon={icon} />}
                {project.psm && <StatusIcon type='neutral' icon={<CheckCircle />} />}
                {(forecast === 'internal' || (project.methods && 'internal' in project.methods)) && (
                    <Badge type='neutral'>{t('common:type.short-internal')}</Badge>
                )}
                {(forecast === 'external' || (project.methods && 'external' in project.methods)) && (
                    <Badge type='neutral'>{t('common:type.short-external')}</Badge>
                )}
                {project.rope_access && <StatusIcon type='neutral' icon={'R'} />}
                {project.internal_scaffold && <StatusIcon type='neutral' icon={'S'} />}
                {project.heavy_cleaning && <StatusIcon type='neutral' icon={'C'} />}
            </div>
            {cls.tooltip && (
                <>
                    <hr />
                    <span className={classes.date}>{date}</span>
                </>
            )}
        </div>
    );
};

export const ProjectCalendar = (props: { onFilter?: (events: CalendarEvent[]) => void }) => {
    const dispatch = useTypedDispatch();
    const { projects } = useTypedSelector(state => ({ projects: state.projectCalendar.data }));
    const [droppedEvent, setDroppedEvent] = React.useState<CalendarEvent>();
    const memoizedEvents = React.useMemo(() => {
        const events: CalendarEvent[] = [];
        projects.forEach(p => {
            if (p.inspection != undefined && p.inspection !== -1)
                events.push({
                    name: p.name,
                    group: p.status!,
                    color: colors[p.status!],
                    draggable: p.status === 'ready to inspect',
                    data: { project: p } as ProjectData,
                    key: events.length,
                    interval: { start: p.estimated_start_date!, end: p.estimated_completed_date! },
                    render: props => <ProjectEvent {...props} />,
                });
            else if (p.plan != undefined) {
                if (p.external_activity_required)
                    events.push({
                        name: p.name,
                        group: 'forecast',
                        color: colors['forecast'],
                        interval: { start: p.external_due_date!, end: p.external_due_date! },
                        draggable: true,
                        key: events.length,
                        data: { project: p, forecast: 'external' } as ProjectData,
                        render: props => <ProjectEvent {...props} />,
                    });
                if (p.internal_activity_required)
                    events.push({
                        name: p.name,
                        group: 'forecast',
                        color: colors['forecast'],
                        interval: { start: p.internal_due_date!, end: p.internal_due_date! },
                        draggable: true,
                        key: events.length,
                        data: { project: p, forecast: 'internal' } as ProjectData,
                        render: props => <ProjectEvent {...props} />,
                    });
            }
        });
        return events;
    }, [projects]);
    const events = React.useMemo(() => {
        const events = [...memoizedEvents];
        if (droppedEvent) events[droppedEvent.key as number] = droppedEvent;
        return events;
    }, [droppedEvent, memoizedEvents]);

    const onDnD = async (event: CalendarEvent, duration: Duration) => {
        const { project, forecast } = event.data as ProjectData;
        const droppedEvent = {
            ...event,
            interval: { start: add(event.interval.start, duration), end: add(event.interval.end, duration) },
        };
        setDroppedEvent(droppedEvent);
        try {
            if (!forecast) {
                await dispatch(actions.project.get(project.inspection!, true));
                dispatch(
                    actions.project.edit({
                        estimated_start_date: droppedEvent.interval.start,
                        estimated_completed_date: droppedEvent.interval.end,
                    }),
                );
                dispatch(actions.project.commit());
                await dispatch(actions.project.update(true));
            } else {
                await dispatch(actions.plan.get(project.plan!, true));
                dispatch(
                    actions.plan.edit(
                        forecast === 'internal'
                            ? { internal_due_date: droppedEvent.interval.start }
                            : { external_due_date: droppedEvent.interval.start },
                    ),
                );
                dispatch(actions.plan.commit());
                await dispatch(actions.plan.update(true));
            }
            await dispatch(actions.projectCalendar.list());
        } finally {
            setDroppedEvent(undefined);
        }
    };

    return (
        <Calendar
            events={events}
            initialView='year'
            onDnD={onDnD}
            onExternalDnD={(e, d) => console.log('externaldnd', { e, d })}
            onIntervalChange={(view, anchor, interval) => {
                const extendedInterval = { start: startOfYear(interval.start), end: endOfYear(interval.end) };
                dispatch(actions.projectCalendar.list(undefined, extendedInterval));
            }}
            onClick={console.log}
            onFilter={props.onFilter}
            // header={
            //     <Button>
            //         <EditOutlined /> Edit Calendar
            //     </Button>
            // }
        />
    );
};
